/**
 * Registry of agent-action *view* capabilities — the render half of a
 * permission-gated AI action. Keyed by the same `kind` as the lifecycle
 * capability in the state package; the generic `AgentActionApprovalPanel`
 * looks a capability up per row to render the bits that differ between
 * action kinds (the card badge, the proposal body, and the settled
 * summary). Everything else about the card/panel is kind-agnostic.
 *
 * `cypher_write` is the first registered view; see `cypherWriteView.tsx`.
 */
import type { ReactNode } from 'react'
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'

export interface AgentActionViewCapability {
  /** Discriminator shared with the proposal's `kind` and the lifecycle capability. */
  kind: string
  /** Capability-specific Apply label, including retry and recovery states. */
  applyButtonLabel?(proposal: InFlightAgentActionProposal, retry: boolean): string
  rejectLabel?: string
  rejectBlockedReason?(proposal: InFlightAgentActionProposal): string | null
  /** Short uppercase pill rendered in the card header (e.g. "create", "link"). */
  badgeLabel(proposal: InFlightAgentActionProposal): string
  /** The expandable proposal body (e.g. the cypher block or evidence layout). */
  renderDetail(proposal: InFlightAgentActionProposal): ReactNode
  /** Effective permission required before this capability's Apply is enabled. */
  requiredApplyPermission?: string
  /** Human-readable explanation shown when requiredApplyPermission is absent. */
  permissionDeniedMessage?: string
  /**
   * Per-proposal Apply gate for kinds whose requirements depend on the
   * proposal itself. Returns the blocking message, or null when Apply is
   * allowed. Takes precedence over `requiredApplyPermission`.
   */
  applyBlockedReason?(
    proposal: InFlightAgentActionProposal,
    permissions: readonly string[] | undefined
  ): string | null
  /**
   * The settled-row summary shown once the user has applied/rejected the
   * proposal (e.g. "Applied — +1 node." or "Rejected — …"). Returns
   * `null` to fall back to nothing.
   */
  renderSettledSummary(proposal: InFlightAgentActionProposal): ReactNode
}

const viewByKind = new Map<string, AgentActionViewCapability>()

export function registerAgentActionView(capability: AgentActionViewCapability): void {
  viewByKind.set(capability.kind, capability)
}

export function getAgentActionView(kind: string): AgentActionViewCapability | undefined {
  return viewByKind.get(kind)
}
